import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FormGroup } from '@angular/forms';

import { getCookie } from '@lib/cookies';
import { CookieKey } from '@lib/cookie-keys.const';
import { downloadBlob, extractResponseBody, getQueryParamsFromRelativeUrl, mapPagination } from '@lib/utils';

import { ServerApi, W2_API_URL } from '@services/server-api';
import { TimeZoneFormatService } from '@app/admin/fleet-card/shared/billing-tz.service';
import { Session } from '@services/session';
import { FleetCardsDataService } from '@services/fleet-cards-data';

import { moment } from '@app/lib/moment';

import {
  IOrder,
  ITransformedOrders,
  IServerOrdersResponse,
  IServerOrder,
  IServerVerifiedShippingAddress,
  IServerVerifiedShippingAddressPayload,
  IVerifiedShippingAddress,
  IServerOrderTransitionDetails,
  IOrderTransitionDetails,
  IShippingAddress,
  IOrderDetails,
  IExtraAttrs,
  IServerSubmitOrder,
  IServerUpdateDraftOrderResponse,
  IServerCreateAndUpdateDraftOrderRequest,
} from './orders.interface';

import {
  CardType,
  IServerValidateCsv,
  IValidateCsv,
  IServerPersonalizationItem,
  IPersonalizationItem,
  IEligibilityCheck,
  IServerEligibilityCheck,
  IValidatedPersonalizedItem,
  IServerValidatedPersonalizedItem,
  IServerAssigneeValidationPayload,
  IServerCardOrder,
  IServerDriversResponse,
  IServerBulkEntitySelectPayload,
  IServerBulkEntitySelectResponse,
  IBulkEntitySelectResponse,
  IBulkEntitySelectEntity,
  IServerBulkEntitySelectEntity,
  IBulkEntitySelectionConfig,
  IEntityForBulkSelection,
  IEntityCountMap,
  IServerEntityCountMap,
} from './checkout/checkout-form-config/interfaces';
import { IVehicleListServerResponse } from '@app/admin/vehicles/list.service';

import { USER_DATE_FORMAT, USER_DATE_TIME_FORMAT } from '@app/lib/moment';
import { ORDER_STATUS } from './orders.constants';
import { ENTITY_TYPE } from '@app/admin/bulk-edits/bulk-edit-admin-settings.interface';
import { IFleetCardCompany } from '@app/admin/fleet-card/shared/fleet-cards-waitlist/fleet-cards-waitlist.interface';
import { CheckoutForm } from './checkout/checkout-form-config/checkout-form';
import { EntityType } from '@lib/entity.const';
import { FIRST_TIME_ORDER_REASON, PersonalizationItemKeys, VALIDATE_ASSIGNEE_ERROR_MAP } from './checkout/checkout-form-config/constants';
import { FleetCardsFeatureFlagService } from '@services/feature-flags/fleet-cards';

const ITEMS_PER_PAGE: number = 25;

const authToken: CookieKey = getCookie(CookieKey.AUTH_TOKEN);

@Injectable({ providedIn: 'root' })
export class FleetCardsOrdersService {
  private verifiedAddresses$: BehaviorSubject<IVerifiedShippingAddress[]> = new BehaviorSubject<IVerifiedShippingAddress[]>(null);

  public processedRecords: number = 0;
  public totalRecords: number = 0;
  public readonly DEFAULT_PER_PAGE_LIMIT: number = 500;
  private isFleetCardsCardOrderImprovementsEnabled: boolean = false;

  constructor(
    private router: Router,
    private api: ServerApi,
    private timeZoneFormat: TimeZoneFormatService,
    private session: Session,
    private http: HttpClient,
    private form: CheckoutForm,
    private fleetCardFeatureFlagService: FleetCardsFeatureFlagService,
    private fleetCardsDataService: FleetCardsDataService,
  ) {
    this.initializeCardOrderFeatureFlags();
  }
  
  private initializeCardOrderFeatureFlags(): void {
    // Adding isFleetCardsCardOrderPublicAppEnabled to control public app card orders roll back
    this.fleetCardsDataService.getCachedCompanyMetadata().subscribe((company: IFleetCardCompany) => {
      this.isFleetCardsCardOrderImprovementsEnabled = 
      this.fleetCardFeatureFlagService.isFleetCardsCardOrderImprovementsEnabled() ||
      (this.fleetCardFeatureFlagService.isFleetCardsCardOrderPublicAppEnabled() && company.cardApplication.publicApp);
    }, (err) => console.error(err));
  }

  public get(params: Map<string, string | number | string[]>): Observable<ITransformedOrders> {
    if (!params.get('per_page')) {
      params = params.set('per_page', ITEMS_PER_PAGE);
    }

    return this.api.get('fleetcard/w1/card_orders', params)
      .pipe(
        map(extractResponseBody),
        map(this.transformGetOrdersData.bind(this)),
      );
  }

  private transformGetOrdersData(data: IServerOrdersResponse): ITransformedOrders {
    const orders: IOrder[] = (data.card_orders || []).map((order: IServerOrder) => this.transformGetOrderData(order));
    return {
      listItems: orders,
      pagination: mapPagination(data, orders.length || 0),
    };
  }

  public getOrder(orderNumber: string): Observable<IOrder> {
    return this.api.get('fleetcard/w1/card_orders/' + orderNumber)
      .pipe(
        map(extractResponseBody),
        map(this.transformGetOrderData.bind(this)),
        tap((order: IOrder) => {
          if (this.isFleetCardsCardOrderImprovementsEnabled) {
            const isReasonFirstOrder: boolean = order.reason === FIRST_TIME_ORDER_REASON.value;
            this.form.isFirstCardOrder.next(isReasonFirstOrder);
            if (isReasonFirstOrder) {
              this.form.firstOrderDetails = order.orderDetails;
            }
          }
        }),
      );
  }

  public verifiedAddresses(): Observable<IVerifiedShippingAddress[]> {
    return this.verifiedAddresses$.pipe(
      map(addresses => addresses ? addresses.filter(a => a !== null && a !== undefined) : []),
    );
  }

  private transformGetOrderData(order: IServerOrder): IOrder {
    const userTimeZone: string = this.session.getUserTimeZone();
    const shippedTransition: IOrderTransitionDetails =
      order.order_transition_details?.find(transition => transition.status === ORDER_STATUS.shipped);
    // Determine the number of days to add based on expedite_shipping
    const daysToAdd: number = order.order_details?.expedite_shipping ? 5 : 10;

    let expectedDeliveryDate: string = '';
    if (order.status === ORDER_STATUS.shipped && shippedTransition) {
      expectedDeliveryDate = this.timeZoneFormat.formatToTimeZoneAndAddDays(
        userTimeZone,
        shippedTransition.date,
        USER_DATE_FORMAT,
        daysToAdd,
      );
    }

    const orderDetailObject: IOrder = {
      orderNumber: order.order_number,
      status: order.status,
      createdAt: this.timeZoneFormat.formatToTimeZone(userTimeZone, order.created_at, 'MM/DD/YYYY'),
      createdBy: order.created_by,
      cancellationReason: order.cancellation_reason,
      providerTrackingNumber: order.provider_tracking_number || order.provider_shipment_number,
      providerTrackingUrl: order.provider_tracking_url,
      expectedDeliveryDate,
      updatedByInternalUserName: order.updated_by_internal_user_name,
      updatedAtByInternalUser:
        this.timeZoneFormat.formatToTimeZone(userTimeZone, order.updated_at_by_internal_user, USER_DATE_FORMAT),
      orderDetails: this.getOrderDetails(order),
      shippingAddress: this.getOrderShippingAddress(order),
      orderTransitionDetails: this.getOrderTransitionDetails(order, userTimeZone),
      extraAttrs: this.getOrderExtraAttrs(order),
      personalizationItems: this.getPersonalizationItems(order),
      entityCountMap: this.getEntityCountMap(order),
      showTrackingLink: this.showTrackingLink(order, userTimeZone),
      reason: order.reason,
    };

    return orderDetailObject;
  }

  private showTrackingLink(order: IServerOrder, userTimeZone: string): boolean {
    const DAYS_TO_ADD_AFTER_DELIVERY = 10;
    const providerTrackingNumber: string = order.provider_tracking_number || order.provider_shipment_number;
    const providerTrackingUrl: string = order.provider_tracking_url;

    if (!(providerTrackingNumber || providerTrackingUrl)) return false;
    switch (order.status) {
    case ORDER_STATUS.shipped:
      return true;
    case ORDER_STATUS.delivered:
      const deliveredTransition =
        order.order_transition_details?.find(transition => transition.status === ORDER_STATUS.delivered);
      if (!deliveredTransition) return false;

      const deliveryPlusTenDays = this.timeZoneFormat.formatToTimeZoneAndAddDays(
        userTimeZone,
        deliveredTransition.date,
        USER_DATE_TIME_FORMAT,
        DAYS_TO_ADD_AFTER_DELIVERY,
      );
      const currentDate = this.timeZoneFormat.formatToTimeZone(
        userTimeZone,
        moment().format(USER_DATE_TIME_FORMAT),
        USER_DATE_TIME_FORMAT,
      );

      return moment(currentDate).isBefore(moment(deliveryPlusTenDays));
    default:
      return false;
    }
  }

  private getPersonalizationItems(order: IServerOrder): IPersonalizationItem[] {
    return order.personalization_items ? order.personalization_items.map((item: IServerPersonalizationItem) => ({
      cardType: item.card_type,
      assignee: item.assignee,
      nameOnCard: item.name_on_card,
      userId: item?.user_id,
      vehicleId: item?.vehicle_id,
      name: item?.name,
      entityId: (item?.entity_id).toString(), // Added for backward compatibility
    })) : [];
  }

  private getEntityCountMap(order: IServerOrder): IEntityCountMap {
    let entityCountMap: IEntityCountMap = {
      errors: 0,
      drivers: 0,
      vehicles: 0,
      fleetUsers: 0,
    };

    const serverEntityCountMap: IServerEntityCountMap = order.entity_count_map;

    if (serverEntityCountMap) {
      entityCountMap = {
        errors: serverEntityCountMap.errors,
        drivers: serverEntityCountMap.drivers,
        vehicles: serverEntityCountMap.vehicles,
        fleetUsers: serverEntityCountMap.fleet_users,
      }
    }

    return entityCountMap;
  }

  private getOrderExtraAttrs(order: IServerOrder): IExtraAttrs {
    return order.extra_attrs ? {
      requestedBy: order.extra_attrs.requested_by,
    } : null;
  }

  private getOrderDetails(order: IServerOrder): IOrderDetails {
    return order.order_details ? {
      cardProductType: order.order_details.card_product_type,
      numberOfCards: order.order_details.number_of_cards,
      personalization: order.order_details.personalization,
      expediteShipping: order.order_details.expedite_shipping,
      contactPersonDetails: order.order_details.contact_person_details ? {
        name: order.order_details.contact_person_details.name,
        mobile: order.order_details.contact_person_details.mobile,
      } : null,
    } : null;
  }

  private getOrderShippingAddress(order: IServerOrder): IShippingAddress {
    return order.shipping_address ? {
      id: order.shipping_address.id,
      addressLine1: order.shipping_address.address_line1,
      addressLine2: order.shipping_address.address_line2,
      city: order.shipping_address.city,
      state: order.shipping_address.state,
      postalCode: order.shipping_address.postal_code,
      country: order.shipping_address.country,
      addressProofs: order.shipping_address?.address_proofs ?? [],
      addressDocumentationRequired: order.shipping_address?.address_documentation_required ?? false,
    } : null;
  }

  private getOrderTransitionDetails(order: IServerOrder, userTimeZone: string): IOrderTransitionDetails[] {
    return order.order_transition_details
      ? order.order_transition_details.map((transitionDetail: IServerOrderTransitionDetails) => ({
        date: this.timeZoneFormat.formatToTimeZone(userTimeZone, transitionDetail.date, USER_DATE_FORMAT),
        status: transitionDetail.status,
      })) : [];
  }

  public createOrder(payload: IServerCardOrder): Observable<string> {
    return this.api.post('fleetcard/w1/card_orders/create_order', { card_order: payload })
      .pipe(
        map(extractResponseBody),
        map(this.transformCreateOrderResponse.bind(this)),
      );
  }

  private transformCreateOrderResponse(response: { id: string }): string {
    return response.id;
  }

  public isCompanyEligible(): Observable<IEligibilityCheck> {
    return this.api.get<IEligibilityCheck>('fleetcard/w1/card_orders/eligibility')
      .pipe(
        map(extractResponseBody),
        map(this.transformEligibilityPayload.bind(this)),
      );
  }

  private transformEligibilityPayload(response: IServerEligibilityCheck): IEligibilityCheck {
    return {
      eligible: response.eligible,
      reason: response.reason ?? null,
      errorCode: response.error_code ?? null,
    };
  }

  public getShippingAddress(status: string = 'verified', addressType?: string ): Observable<IVerifiedShippingAddress[]> {
    const params = new Map<string, string>().set('status', status);
    if (addressType) {
      params.set('address_type', addressType);
    }
    return this.api.get<IVerifiedShippingAddress[]>('fleetcard/w1/shipping_addresses', params)
      .pipe(
        map(extractResponseBody),
        map(this.transformVerifiedShippingAddress.bind(this)),
        tap((addresses: IVerifiedShippingAddress[]) => this.verifiedAddresses$.next(addresses)),
      );
  }

  private transformVerifiedShippingAddress(response: IServerVerifiedShippingAddressPayload): IVerifiedShippingAddress[] {
    return response.shipping_addresses.map((addr: IServerVerifiedShippingAddress): IVerifiedShippingAddress => {
      return {
        addressDetails: {
          addressLine1: addr.address_details.address_line1,
          addressLine2: addr.address_details.address_line2 ?? null,
          city: addr.address_details.city,
          country: addr.address_details.country,
          postalCode: addr.address_details.postal_code,
          state: addr.address_details.state,
          addressProofs: addr.address_details.address_proofs ?? [],
        },
        id: addr.id,
      };
    });
  }

  private transformValidateCsvResponse(response: IServerValidateCsv): IValidateCsv {
    return {
      personalizationItems: response.personalization_items.map((item: IServerPersonalizationItem): IPersonalizationItem => {
        return {
          cardType: item.card_type,
          userId: item.user_id,
          vehicleId: item.vehicle_id,
          nameOnCard: item.name_on_card,
          name: item.name,
        };
      }),
      valid: response.valid,
      url: response.url,
      id: response.id,
    };
  }

  public validateCsv(file: File): Observable<IValidateCsv> {
    const formData: FormData = new FormData();
    formData.append('file', file);

    const headers = new HttpHeaders({
      'X-Web-User-Auth': authToken,
      'enctype': 'multipart/formdata',
    });

    return this.http.post(`${W2_API_URL}/fleetcard/w1/card_orders/validate_csv`, formData, { headers }).pipe(
      map(this.transformValidateCsvResponse.bind(this)),
    );
  }

  public validateAssignee(assignee: IServerPersonalizationItem[]): Observable<IValidatedPersonalizedItem[]> {
    return this.api.post('fleetcard/w1/card_orders/validate_assignees', { assignees: assignee })
      .pipe(
        map(extractResponseBody),
        map(this.transformValidateAssigneeResponse.bind(this)),
      );
  }

  private transformValidateAssigneeResponse(response: IServerAssigneeValidationPayload): IValidatedPersonalizedItem[] {
    return response.assignees.map((assignee: IServerValidatedPersonalizedItem): IValidatedPersonalizedItem => {
      return {
        userId: assignee.user_id ?? null,
        vehicleId: assignee.vehicle_id ?? null,
        nameOnCard: assignee.name_on_card,
        reason: assignee.reason,
        eligible: assignee.eligible,
      };
    });
  }

  private transformBulkEntitiesResponse(response: IServerBulkEntitySelectResponse): IBulkEntitySelectResponse {
    return {
      entities: response.results.map((entity: IServerBulkEntitySelectEntity) => {
        return {
          id: entity.entity_id,
          nameOnCard: entity.name_on_card,
          entityType: entity.entity_type,
          assignee: entity.assignee,
        };
      }),
      pageNo: response.page_no,
      total: response.total,
      perPage: response.per_page,
    };
  }

  public fetchEntityListData(
    url: string,
    params: Map<string, string | number | string[]>,
  ): Observable<IServerDriversResponse | IVehicleListServerResponse> {
    return this.api.get<IServerDriversResponse | IVehicleListServerResponse>(url, params).pipe(map(extractResponseBody));
  }

  public fetchIdentities(
    url: string,
    params: Map<string, string | number | string[] | boolean>,
  ): Observable<number[]> {
    return this.api.get<number[]>(url, params).pipe(map(extractResponseBody));
  }

  public getCardIdSuggestions(cardType: ENTITY_TYPE, cardId: string): Observable<string[]> {
    const paramsMap: Map<string, string> = new Map<string, string>([
      ['card_type', cardType],
      ['card_id', cardId],
      ['count', '3'],
    ]);
    return this.api.get('fleetcard/w1/card_orders/card_id_suggestions', paramsMap).pipe(
      map(extractResponseBody),
      map((res: { suggestions: string[] }) =>
        res.suggestions.map(suggestion =>
          cardType === ENTITY_TYPE.VEHICLE ? suggestion.substring(4) : suggestion,
        ),
      ),
    );
  }

  public getEntitiesForPersonalization(payload: IServerBulkEntitySelectPayload): Observable<IBulkEntitySelectResponse> {
    return this.api.post<IBulkEntitySelectResponse>(`companies/${this.session.getCompany().id}/entities`, payload).pipe(
      map(extractResponseBody),
      map(this.transformBulkEntitiesResponse),
    );
  }

  public getAllEntitiesForPersonalization(): Observable<IBulkEntitySelectResponse> {
    const driverPayload: IServerBulkEntitySelectPayload = this.getBulkSelectionPayloadForType(ENTITY_TYPE.DRIVER);
    const vehiclePayload: IServerBulkEntitySelectPayload = this.getBulkSelectionPayloadForType(ENTITY_TYPE.VEHICLE);
    const fleetUserPayload: IServerBulkEntitySelectPayload = this.getBulkSelectionPayloadForType(ENTITY_TYPE.FLEET_USER);

    const totalDrivers: number = driverPayload.entities.entity_ids.length;
    const totalVehicles: number = vehiclePayload.entities.entity_ids.length;
    const totalFleetUsers: number = fleetUserPayload.entities.entity_ids.length;

    this.totalRecords = totalDrivers + totalVehicles + totalFleetUsers;

    return forkJoin({
      driverResponse: this.fetchEntitiesRecursively(driverPayload, [], 1),
      fleetUserResponse: this.fetchEntitiesRecursively(fleetUserPayload, [], 1),
      vehicleResponse: this.fetchEntitiesRecursively(vehiclePayload, [], 1),
    }).pipe(
      map(({ driverResponse, fleetUserResponse, vehicleResponse }) => {
        return {
          entities: [...driverResponse.entities, ...fleetUserResponse.entities, ...vehicleResponse.entities],
          pageNo: fleetUserResponse.pageNo,
          total: driverResponse.total + vehicleResponse.total + fleetUserResponse.total,
          perPage: fleetUserResponse.perPage,
        };
      })
    );
  }

  private fetchEntitiesRecursively(
    payload: IServerBulkEntitySelectPayload,
    accumulatedEntities: IBulkEntitySelectEntity[],
    currentPage: number,
  ): Observable<IBulkEntitySelectResponse> {
    if (payload.entities.entity_ids.length === 0) {
      return of({
        entities: [],
        pageNo: 1,
        total: 0,
        perPage: this.DEFAULT_PER_PAGE_LIMIT,
      });
    }
    const updatedPayload: IServerBulkEntitySelectPayload = { ...payload, page_no: currentPage };

    return this.getEntitiesForPersonalization(updatedPayload).pipe(
      tap(response => {
        const newEntities: IBulkEntitySelectEntity[] = response.entities;
        this.processedRecords += newEntities.length;
        const personalizationItems: IPersonalizationItem[] = newEntities.map(entity => {
          return {
            ...(entity.entityType === EntityType.VEHICLE && { vehicleId: entity.id.toString() }),
            ...(entity.entityType === EntityType.DRIVER && { userId: entity.id.toString() }),
            ...(entity.entityType === EntityType.FLEET_USER && { userId: entity.id.toString() }),
            cardType: entity.entityType as CardType,
            assignee: entity.assignee,
            nameOnCard: entity.entityType === EntityType.VEHICLE ? entity.nameOnCard.substring(4) : entity.nameOnCard,
            name: entity.assignee,
          };
        });
        const formArray: FormGroup[] = this.form.appendPersonalizationItems(personalizationItems);
        const entityLookup: Map<string, FormGroup> = new Map<string, FormGroup>();
        formArray.forEach((group: FormGroup) => {
          const entityId: string =
            group.get(PersonalizationItemKeys.userId)?.value ||
            group.get(PersonalizationItemKeys.vehicleId)?.value;

          if (entityId) {
            entityLookup.set(entityId, group);
          }
        });

        // Validate assignees for the newly added personalization items
        const assignees: IServerPersonalizationItem[] = this.form.getBulkValidateEntryPayload(formArray);
        this.validateAssignee(assignees).subscribe({
          next: (responses: IValidatedPersonalizedItem[]) => {
            responses.forEach(validationResponse => {
              const entityId: string = validationResponse.userId || validationResponse.vehicleId;
              const group: FormGroup = entityLookup.get(entityId);

              if (validationResponse.eligible) {
                group.controls[PersonalizationItemKeys.isValidated].setValue(true);
                group.setErrors(null);
              } else {
                group.get(PersonalizationItemKeys.nameOnCard).markAsDirty();
                group.get(PersonalizationItemKeys.entity).markAsDirty();
                group.controls[PersonalizationItemKeys.isValidated].setValue(false);
                group.setErrors({ validationError: VALIDATE_ASSIGNEE_ERROR_MAP.get(validationResponse.reason) });
              }
            });
          }, error: () => {
            formArray.forEach(group => {
              group.get(PersonalizationItemKeys.nameOnCard).markAsDirty();
              group.get(PersonalizationItemKeys.entity).markAsDirty();
              group.controls[PersonalizationItemKeys.isValidated].setValue(false);
            });
          },
        });
      }),
      switchMap(res => {
        const allEntities: IBulkEntitySelectEntity[] = [...accumulatedEntities, ...res.entities];
        const totalPages: number = Math.ceil(res.total / res.perPage);

        if (currentPage < totalPages) {
          return this.fetchEntitiesRecursively(payload, allEntities, currentPage + 1);
        }

        return of({
          entities: allEntities,
          pageNo: res.pageNo,
          total: res.total,
          perPage: res.perPage,
        });
      }),
    );
  }

  private getBulkSelectionPayloadForType(entityType: string): IServerBulkEntitySelectPayload {
    const config: IBulkEntitySelectionConfig = this.form.bulkEntitySelectionConfig.find(c => c.key === entityType);

    const invalidEntityIDs: string[] = config.entities
      .filter((entity: IEntityForBulkSelection) => entity.isDisabled)
      .map((entity: IEntityForBulkSelection) => entity.companyId);

    const validEntityIds: number[] = Object.keys(config.selectedEntities)
      .filter(id => !invalidEntityIDs.includes(id))
      .map(id => Number(id));

    return {
      page_no: 1,
      per_page: this.DEFAULT_PER_PAGE_LIMIT,
      entities: {
        entity_type: config.key,
        entity_ids: validEntityIds,
        autofill_type: config.selectedAutoFillOption,
      },
    };
  }

  /**
   * Creates or updates draft order details by sending a POST request to the server.
   *
   * @param orderDetails - The details of the draft order to create or update, conforming to the `IServerSubmitOrder` interface.
   * @returns An `Observable<number>` that emits the ID of the created or updated draft order.
   */
  public createUpdateDraftOrderDetails(orderDetails: IServerCreateAndUpdateDraftOrderRequest): Observable<string> {
    return this.api.post('fleetcard/w1/card_orders/draft', orderDetails)
      .pipe(
        map(extractResponseBody),
        map(this.transformDraftOrderDetails.bind(this))
      );
  }

  /**
   * Downloads the card customization CSV for a given order number.
   * @param orderNumber - The 8 digit order number for which to download the CSV.
   * @returns An Observable that emits the response body of the CSV download.
   */
  public getDownloadableCardCustomizationCSV(orderNumber: number | string): Observable<any> {
    return this.api.get(`fleetcard/w1/card_orders/${orderNumber}/download_csv`)
      .pipe(
        map(extractResponseBody),
      );
  }

  /**
   * Submits the details of a order (which may or may not be in draft) for the specified order number.
   *
   * Sends a POST request to the backend API to submit the provided order details.
   * The response is processed and transformed before being returned as an observable.
   *
   * @param orderNumber - The unique identifier of the order to submit.
   * @param orderDetails - The details of the order to be submitted, conforming to the IServerSubmitOrder interface.
   * @returns An Observable that emits the order ID (number) upon successful submission.
   */
  public submitOrderDetails(orderNumber: string, orderDetails: IServerSubmitOrder | IServerSubmitOrder): Observable<string> {
    return this.api.post(`fleetcard/w1/card_orders/${orderNumber}/submit/`, orderDetails)
      .pipe(
        map(extractResponseBody),
        map(this.transformDraftOrderDetails.bind(this))
      );
  }

  private transformDraftOrderDetails(response: IServerUpdateDraftOrderResponse): string {
    return response.id.toString();
  }
}